/* eslint-disable arrow-body-style */
/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import { isPlatformWeb } from 'renative';
import { useTranslation } from 'react-i18next';

import { View, Text } from '@24i/nxg-sdk-quarks';
import { useTheme } from '@24i/nxg-sdk-higgs';
import BackButton from '@24i/nxg-sdk-gluons/src/components/buttons/BackButton';
import {
    getTelegramWebApp,
    getTelegramWebAppTopFullScreen,
    isTelegramAVersion,
    isTelegramKVersion,
    initializeTelegramBackButton,
    isWebMobile,
} from '@24i/nxg-sdk-higgs/src/utils';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { useCurrentWebPageConfig } from '@24i/nxg-sdk-smartott/src/hooks/useCurrentWebPageConfig';
import { useShouldShowTopNavbar } from '@24i/nxg-sdk-smartott/src/hooks/useShouldShowTopNavbar';

import getStyles from './styles';
import { WEB_HEADER_TEST_IDS, WithWebHeaderProps } from './types';
import { StickyBackHeader } from '../StickyBackHeader';

export const withWebHeader = <P extends WithWebHeaderProps>(Component: React.ComponentType<P>) => {
    return (props) => {
        const {
            webHeaderTitle,
            renderActionButton,
            fullWidth = false,
            backButtonTitle,
            hasMargin = true,
            forceShowHeader = false,
            forceDisable = false,
            onBackPressOverride,
            variant,
            noVerticalMargin,
            isLoading,
            isBackButtonHeaderText,
        } = props;

        // Early abort if not on web platform or if forceDisable is true
        if (!isPlatformWeb || forceDisable) {
            return <Component {...props} />;
        }

        const { theme } = useTheme();
        const router = useRouter();
        const { t } = useTranslation();
        const { shouldShowTopNavbar } = useShouldShowTopNavbar();
        const { currentPageConfig } = useCurrentWebPageConfig();
        const styles = getStyles(theme, hasMargin, noVerticalMargin);
        const TelegramWebApp = getTelegramWebApp();
        const isTelegramK = isTelegramKVersion();

        // Early abort for dynamic paddingTop if not TelegramWebApp
        const isTelegram = !!TelegramWebApp;
        const isTelegramA = isTelegramAVersion();
        const isFullscreen = TelegramWebApp?.isFullscreen === true;

        // Calculate appropriate padding:
        // - For Telegram A non-fullscreen: use ZERO padding regardless of screen width (fixes weird spacing issue)
        // - For Telegram K or Telegram A fullscreen: use normal calculation
        // - For non-Telegram: use 0
        let initialPadding = 0;
        if (isTelegram && !(isTelegramA && !isFullscreen)) {
            initialPadding = getTelegramWebAppTopFullScreen();
        }

        const [paddingTop, setPaddingTop] = useState(initialPadding);

        useEffect(() => {
            if (!isTelegram) return () => {};

            function updatePaddingTop() {
                // Use the same conditional logic for dynamic updates
                // CRITICAL: For small screen widths, especially with bottom tab bar,
                // we must use ZERO padding for Telegram A non-fullscreen
                const newPadding =
                    isTelegramA && !isFullscreen
                        ? 0 // Always ZERO padding for Telegram A non-fullscreen
                        : getTelegramWebAppTopFullScreen();

                setPaddingTop(newPadding);
            }

            if (typeof TelegramWebApp.onEvent === 'function') {
                TelegramWebApp.onEvent('viewportChanged', updatePaddingTop);
            }
            window.addEventListener('resize', updatePaddingTop);

            return () => {
                if (typeof TelegramWebApp.offEvent === 'function') {
                    TelegramWebApp.offEvent('viewportChanged', updatePaddingTop);
                }
                window.removeEventListener('resize', updatePaddingTop);
            };
        }, [TelegramWebApp, isTelegram, isTelegramA, isFullscreen]);

        const onBack = onBackPressOverride || router?.back;

        const containingMenuTitle = router?.query?.containingMenuTitle
            ? `${t('common.backTo')} ${router.query.containingMenuTitle}`
            : undefined;
        const backButtonText = backButtonTitle || containingMenuTitle;
        const showHeader = forceShowHeader || currentPageConfig?.optionsWeb?.headerShown;

        useEffect(() => {
            const isHome =
                router?.asPath === '/' || router?.asPath === '' || router?.asPath === '/home';

            // Early exit if no Telegram WebApp
            if (!TelegramWebApp || !TelegramWebApp.BackButton) {
                return () => {}; // Return empty cleanup function for TypeScript
            }

            // For home page, always hide back button for both K and A
            if (isHome) {
                // Hide back button consistently for both K and A
                try {
                    // @ts-ignore - First try direct property access (works for K)
                    TelegramWebApp.BackButton.isVisible = false;
                } catch (e) {
                    // Ignore error for A version
                }

                // Always call hide() - works for both K and A
                if (typeof TelegramWebApp.BackButton.hide === 'function') {
                    try {
                        TelegramWebApp.BackButton.hide();
                    } catch (e) {
                        // Ignore hide errors
                    }
                }

                return () => {}; // Empty cleanup for home page
            }

            // For non-home pages with an onBack handler, use our centralized utility function
            if (onBack) {
                // In fullscreen mode for both K and A:
                // - Hide custom back button, show native Telegram button
                // In non-fullscreen mode for both K and A:
                // - Show custom back button, hide native Telegram button
                const shouldUseNativeButton = isFullscreen;

                // Use our centralized back button initialization function
                const cleanup = initializeTelegramBackButton(
                    TelegramWebApp,
                    onBack,
                    isTelegramK,
                    shouldUseNativeButton
                );

                // Ensure we always return a cleanup function
                return typeof cleanup === 'function' ? cleanup : () => {};
            }

            return () => {}; // Default empty cleanup function
        }, [TelegramWebApp, onBack, router?.asPath, isTelegramK]);

        // No special debug needed anymore

        if (variant === 'sticky') {
            // Use existing Telegram variables (to avoid redeclaration)
            const isMobileWeb = isWebMobile();

            // This is the most important rule - Telegram must use its native back button
            if (isTelegram) {
                // Handle Telegram back button (if we have onBack)
                if (TelegramWebApp && onBack) {
                    // Initialize Telegram back button without rendering StickyHeader
                    initializeTelegramBackButton(TelegramWebApp, onBack, isTelegramK, true);
                }

                // We still need to return the Component, but let's check if we need to show a title
                // For Telegram, we'll use a special approach: add a simple header with just the title text
                if (props.forceShowHeader && webHeaderTitle) {
                    return (
                        <View style={fullWidth ? styles.containerFullWidth : styles.container}>
                            {paddingTop > 0 && isMobileWeb && (
                                <View style={{ height: paddingTop }} />
                            )}
                            <View style={styles.headerContainer}>
                                <View
                                    style={{ ...styles.titleContainer, justifyContent: 'center' }}
                                >
                                    <Text
                                        testID={WEB_HEADER_TEST_IDS.PAGE_TITLE}
                                        style={{
                                            ...styles.headerText,
                                            ...styles.headerTextTelegram,
                                        }}
                                    >
                                        {webHeaderTitle}
                                    </Text>
                                </View>
                            </View>
                            <Component {...props} />
                        </View>
                    );
                }

                // If no header needed, just return the component
                return <Component {...props} />;
            }

            // For mobile web (not Telegram), ALWAYS show StickyHeader
            // This is very important - mobile web needs the StickyHeader
            if (isMobileWeb) {
                return (
                    <StickyBackHeader
                        title={webHeaderTitle || backButtonText}
                        onBack={onBack}
                        iconSize={styles.backButtonIcon.fontSize || 16}
                    >
                        <Component {...props} />
                    </StickyBackHeader>
                );
            }

            // Standard case: Regular web - show StickyHeader if top navbar isn't shown
            if (!shouldShowTopNavbar) {
                return (
                    <StickyBackHeader
                        title={backButtonText}
                        onBack={onBack}
                        iconSize={styles.backButtonIcon.fontSize || 16}
                    >
                        <Component {...props} />
                    </StickyBackHeader>
                );
            }

            // Default - no StickyHeader needed
            return <Component {...props} />;
        }

        return (
            <View style={fullWidth ? styles.containerFullWidth : styles.container}>
                {/* Only add padding spacer for Telegram when needed - padding is ZERO for Telegram A non-fullscreen */}
                {isTelegram && paddingTop > 0 && <View style={{ height: paddingTop }} />}
                <View style={styles.headerContainer}>
                    <View style={styles.titleContainer}>
                        {backButtonText &&
                            (!isTelegram || (isTelegram && isWebMobile() && !isFullscreen)) && (
                                <BackButton
                                    onPress={onBack}
                                    iconName="onPrimary-back"
                                    iconSize={styles.backButtonIcon.fontSize || 16}
                                    title={containingMenuTitle}
                                    additionalTextStyles={
                                        isBackButtonHeaderText
                                            ? styles.headerText
                                            : styles.backButtonText
                                    }
                                    additionalIconStyles={styles.backButtonIcon}
                                    additionalTextContainerStyles={styles.backButtonIconContainer}
                                    titleNumberOfLines={2}
                                />
                            )}
                        {showHeader && !isLoading && (
                            <Text
                                testID={WEB_HEADER_TEST_IDS.PAGE_TITLE}
                                style={backButtonText ? styles.headerText : styles.headerTextLarge}
                            >
                                {webHeaderTitle || currentPageConfig?.menuTitle}
                            </Text>
                        )}
                    </View>
                    <View style={styles.actionButton}>
                        {renderActionButton && renderActionButton()}
                    </View>
                </View>
                <Component {...props} />
            </View>
        );
    };
};
